import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { ScrollArea } from '../ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from '../ui/select';
import { 
  ArrowRight, 
  Check, 
  X, 
  RefreshCw, 
  Edit3, 
  Trash2, 
  Merge 
} from 'lucide-react';

const DropdownStandardizeDialog = ({
  open,
  onOpenChange,
  columnName,
  values,
  editingValueIndex,
  setEditingValueIndex,
  editingNewValue,
  setEditingNewValue,
  onRename,
  onMerge,
  onDelete,
  onRestore,
  onSave,
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Standardize Dropdown Values</DialogTitle>
          <DialogDescription>
            View, rename, merge, or delete unique values in &quot;{columnName}&quot;
          </DialogDescription>
        </DialogHeader>
        
        <div className="flex-1 overflow-hidden py-4 space-y-4">
          {/* Summary */}
          <div className="grid grid-cols-4 gap-4 p-3 bg-slate-50 rounded-lg text-sm">
            <div className="text-center">
              <p className="text-2xl font-bold text-slate-800">{values.length}</p>
              <p className="text-slate-500">Total Values</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-emerald-600">{values.filter(v => v.action === 'keep').length}</p>
              <p className="text-slate-500">Keep</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-blue-600">{values.filter(v => v.action === 'rename' || v.action === 'merge').length}</p>
              <p className="text-slate-500">Modify</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-red-600">{values.filter(v => v.action === 'delete').length}</p>
              <p className="text-slate-500">Delete</p>
            </div>
          </div>
          
          {/* Value List */}
          <ScrollArea className="h-[400px] border border-slate-200 rounded-lg">
            <div className="p-2 space-y-2">
              {values.map((item, index) => (
                <div 
                  key={index} 
                  className={`flex items-center gap-3 p-3 rounded-lg border ${
                    item.action === 'delete' ? 'bg-red-50 border-red-200' :
                    item.action === 'rename' || item.action === 'merge' ? 'bg-blue-50 border-blue-200' :
                    'bg-white border-slate-200'
                  }`}
                >
                  {/* Original Value */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className={`font-mono text-sm truncate ${item.action === 'delete' ? 'line-through text-red-500' : ''}`}>
                        {item.original}
                      </span>
                      {(item.action === 'rename' || item.action === 'merge') && (
                        <>
                          <ArrowRight className="w-4 h-4 text-blue-500 flex-shrink-0" />
                          <span className="font-mono text-sm text-blue-700 font-medium truncate">{item.display}</span>
                        </>
                      )}
                    </div>
                  </div>
                  
                  {/* Actions */}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {item.action !== 'keep' ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onRestore(index)}
                        className="text-slate-500 hover:text-slate-700"
                      >
                        <RefreshCw className="w-4 h-4" />
                      </Button>
                    ) : (
                      <>
                        {/* Rename */}
                        {editingValueIndex === index ? (
                          <div className="flex items-center gap-2">
                            <Input
                              value={editingNewValue}
                              onChange={(e) => setEditingNewValue(e.target.value)}
                              className="h-8 w-32"
                              placeholder="New value"
                              autoFocus
                            />
                            <Button
                              size="sm"
                              onClick={() => onRename(index, editingNewValue)}
                              className="h-8"
                            >
                              <Check className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => { setEditingValueIndex(null); setEditingNewValue(''); }}
                              className="h-8"
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        ) : (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => { setEditingValueIndex(index); setEditingNewValue(item.original); }}
                              title="Rename"
                            >
                              <Edit3 className="w-4 h-4" />
                            </Button>
                            
                            {/* Merge */}
                            <Select onValueChange={(val) => onMerge(index, val)}>
                              <SelectTrigger className="w-8 h-8 p-0 border-0 hover:bg-slate-100">
                                <Merge className="w-4 h-4 text-slate-500" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="__merge_placeholder__" disabled>Merge into...</SelectItem>
                                {values
                                  .filter((v, i) => i !== index && v.action !== 'delete')
                                  .map((v, i) => (
                                    <SelectItem key={i} value={v.original}>{v.original}</SelectItem>
                                  ))
                                }
                              </SelectContent>
                            </Select>
                            
                            {/* Delete */}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => onDelete(index)}
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={onSave} data-testid="save-dropdown-btn">
            Apply Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DropdownStandardizeDialog;
